import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
  Table,
  Button,
  Alert,
  Link,
  CircularProgress,
  IconButton,
  Tooltip,
  Modal,
  ModalDialog,
  ModalClose,
} from '@mui/joy';
import {
  TrendUp as TrendingUpOutlined,
  Users as GroupOutlined,
  CurrencyDollar as AttachMoneyOutlined,
  LinkSimple as LinkOutlined,
  Copy as ContentCopyOutlined,
  Calendar as CalendarTodayOutlined,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiClient } from '../../lib/api';
import { AffiliatesAPI } from '../../lib/apiAffiliates';
import { format } from 'date-fns';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';

interface AffiliateProfile {
  id: string;
  user_name: string;
  user_email: string;
  referral_code: string;
  commission_rate: number;
  total_referrals: number;
  successful_referrals: number;
  total_commissions_earned: number;
  total_commissions_paid: number;
  pending_commissions: number;
  referral_url: string;
  status: string;
  created_at: string;
  is_payment_ready: boolean;
  missing_payment_info: string[];
}

interface ReferralTracking {
  id: string;
  organization_name: string;
  referral_source: string;
  signup_completed_at: string;
  first_subscription_at: string | null;
  has_active_subscription: boolean;
  created_at: string;
}

interface CommissionRecord {
  id: string;
  organization_name: string;
  commission_period: string;
  subscription_tier: string;
  commission_amount: number;
  final_commission_amount: number;
  payment_status: string;
  payment_date: string | null;
  created_at: string;
}

interface DashboardData {
  profile: AffiliateProfile;
  recent_referrals: ReferralTracking[];
  recent_commissions: CommissionRecord[];
  monthly_stats: {
    referrals_this_month: number;
    commissions_this_month: number;
    pending_payment: number;
  };
  yearly_stats: {
    referrals_this_year: number;
    commissions_this_year: number;
  };
}

export const AffiliateDashboard: React.FC = () => {
  const { data: dashboardData, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['affiliate-dashboard'],
    queryFn: async (): Promise<DashboardData> => {
      const res = await apiClient.get('/api/affiliates/profiles/dashboard/');
      return res.data as DashboardData;
    },
  });

  // Commission History modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyRecords, setHistoryRecords] = useState<any[]>([]);

  // Referral Analytics modal state
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<Awaited<
    ReturnType<typeof AffiliatesAPI.getPerformanceMetrics>
  > | null>(null);

  const copyReferralLink = () => {
    if (dashboardData?.profile.referral_url) {
      navigator.clipboard.writeText(dashboardData.profile.referral_url);
      toast.success('Referral link copied to clipboard');
    }
  };

  const handleViewCommissionHistory = async () => {
    setHistoryModalOpen(true);
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      const records = await AffiliatesAPI.getCommissionRecords();
      setHistoryRecords(records);
    } catch (err) {
      const message = 'Failed to load commission history. Please try again.';
      setHistoryError(message);
      toast.error(message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewReferralAnalytics = async () => {
    setAnalyticsModalOpen(true);
    setAnalyticsError(null);
    setAnalyticsLoading(true);
    try {
      const metrics = await AffiliatesAPI.getPerformanceMetrics();
      setAnalyticsData(metrics);
    } catch (err) {
      const message = 'Failed to load referral analytics. Please try again.';
      setAnalyticsError(message);
      toast.error(message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        Error loading affiliate dashboard. Please try again later.
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert color="warning">
        No affiliate data found. Please contact support if you believe this is an error.
      </Alert>
    );
  }

  const { profile, recent_referrals, recent_commissions, monthly_stats, yearly_stats } = dashboardData;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography level="h2">
              Affiliate Dashboard
            </Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
            Welcome back, {profile.user_name}! Here's your affiliate performance overview.
          </Typography>
        </Box>

        {/* Status Alert */}
        {profile.status !== 'active' && (
          <Alert color="warning">
            Your affiliate account is currently {profile.status}. Contact support for more information.
          </Alert>
        )}

        {/* Payment Information Alert */}
        {!profile.is_payment_ready && profile.status === 'active' && (
          <Alert 
            color="warning" 
            startDecorator={<AttachMoneyOutlined />}
            endDecorator={
              <Button 
                size="sm" 
                variant="outlined" 
                color="warning"
                onClick={() => window.location.href = '/dashboard/affiliates/complete-profile'}
              >
                Complete Profile
              </Button>
            }
          >
            <Box>
              <Typography level="title-md" sx={{ mb: 1 }}>
                Payment Information Required
              </Typography>
              <Typography level="body-sm">
                Please complete your payment information to receive commission payments. Missing:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                {profile.missing_payment_info.map((item, index) => (
                  <Typography component="li" level="body-sm" key={index}>
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Alert>
        )}

        {/* Key Metrics */}
        <Grid container spacing={3}>
          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'primary.100' }}>
                    <GroupOutlined color="var(--joy-palette-primary-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">{profile.total_referrals}</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Total Referrals
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="success" sx={{ mt: 1 }}>
                  {profile.successful_referrals} successful conversions
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'success.100' }}>
                    <AttachMoneyOutlined color="var(--joy-palette-success-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">
                      ${profile.total_commissions_earned.toLocaleString()}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Total Earned
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="success" sx={{ mt: 1 }}>
                  ${profile.total_commissions_paid.toLocaleString()} paid out
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'warning.100' }}>
                    <TrendingUpOutlined color="var(--joy-palette-warning-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">
                      ${monthly_stats.commissions_this_month.toLocaleString()}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      This Month
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                  {monthly_stats.referrals_this_month} referrals
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'info.100' }}>
                    <CalendarTodayOutlined color="var(--joy-palette-info-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">
                      ${profile.pending_commissions.toLocaleString()}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Pending Payment
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                  Next payment cycle
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Referral Link Card */}
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <LinkOutlined />
              <Typography level="title-md">Your Referral Link</Typography>
              <Chip size="sm" variant="soft" color="primary">
                {profile.commission_rate}% Commission
              </Chip>
            </Stack>
            
            <Stack direction="row" spacing={1} alignItems="center">
              <Link 
                href={profile.referral_url} 
                target="_blank"
                sx={{ 
                  flex: 1, 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 'sm',
                  textDecoration: 'none',
                  fontSize: 'sm',
                  fontFamily: 'monospace'
                }}
              >
                {profile.referral_url}
              </Link>
              <Tooltip title="Copy link">
                <IconButton onClick={copyReferralLink} variant="outlined" size="sm">
                  <ContentCopyOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
            
            <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
              Share this link with potential ApexOS customers to earn commissions on their subscriptions.
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Recent Referrals */}
          <Grid xs={12} lg={6}>
            <Card variant="outlined" sx={{ height: '400px' }}>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Recent Referrals
                </Typography>
                
                {recent_referrals.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                      No referrals yet. Start sharing your referral link!
                    </Typography>
                  </Box>
                ) : (
                  <Table size="sm" sx={{ mt: 2 }}>
                    <thead>
                      <tr>
                        <th>Organization</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent_referrals.map((referral: any) => (
                        <tr key={referral.id}>
                          <td>{referral.organization_name}</td>
                          <td>
                            <Chip 
                              size="sm" 
                              variant="soft" 
                              color={referral.has_active_subscription ? 'success' : 'neutral'}
                            >
                              {referral.has_active_subscription ? 'Active' : 'Signed Up'}
                            </Chip>
                          </td>
                          <td>
                            {format(new Date(referral.created_at), 'MMM d, yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Commissions */}
          <Grid xs={12} lg={6}>
            <Card variant="outlined" sx={{ height: '400px' }}>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Recent Commissions
                </Typography>
                
                {recent_commissions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                      No commissions yet. Keep referring customers!
                    </Typography>
                  </Box>
                ) : (
                  <Table size="sm" sx={{ mt: 2 }}>
                    <thead>
                      <tr>
                        <th>Organization</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent_commissions.map((commission: any) => (
                        <tr key={commission.id}>
                          <td>{commission.organization_name}</td>
                          <td>${commission.final_commission_amount.toLocaleString()}</td>
                          <td>
                            <Chip 
                              size="sm" 
                              variant="soft" 
                              color={
                                commission.payment_status === 'paid' ? 'success' : 
                                commission.payment_status === 'approved' ? 'warning' : 'neutral'
                              }
                            >
                              {commission.payment_status}
                            </Chip>
                          </td>
                          <td>{commission.commission_period}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Quick Actions
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Tooltip title="Coming soon">
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    startDecorator={<LinkOutlined />}
                    disabled
                    endDecorator={
                      <Chip size="sm" variant="soft" color="primary">
                        Coming soon
                      </Chip>
                    }
                  >
                    Marketing Materials
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<AttachMoneyOutlined />}
                onClick={handleViewCommissionHistory}
              >
                Commission History
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<GroupOutlined />}
                onClick={handleViewReferralAnalytics}
              >
                Referral Analytics
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Year Overview */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 3 }}>
              Year to Date Performance
            </Typography>
            <Grid container spacing={4}>
              <Grid xs={6}>
                <Stack>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Referrals This Year
                  </Typography>
                  <Typography level="h3" color="primary">
                    {yearly_stats.referrals_this_year}
                  </Typography>
                </Stack>
              </Grid>
              <Grid xs={6}>
                <Stack>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Commissions This Year
                  </Typography>
                  <Typography level="h3" color="success">
                    ${yearly_stats.commissions_this_year.toLocaleString()}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Stack>

      {/* Commission History Modal */}
      <Modal open={historyModalOpen} onClose={() => setHistoryModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 720, width: '100%' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Commission History
          </Typography>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyError ? (
            <Alert color="danger">{historyError}</Alert>
          ) : historyRecords.length === 0 ? (
            <Alert color="neutral">No commission records found yet.</Alert>
          ) : (
            <Box sx={{ overflow: 'auto', maxHeight: '60vh' }}>
              <Table size="sm" stickyHeader>
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Period</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRecords.map((record: any) => (
                    <tr key={record.id}>
                      <td>{record.organization_name}</td>
                      <td>{record.commission_period}</td>
                      <td>${Number(record.final_commission_amount).toLocaleString()}</td>
                      <td>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={
                            record.payment_status === 'paid'
                              ? 'success'
                              : record.payment_status === 'approved'
                              ? 'warning'
                              : 'neutral'
                          }
                        >
                          {record.payment_status}
                        </Chip>
                      </td>
                      <td>
                        {record.payment_date
                          ? format(new Date(record.payment_date), 'MMM d, yyyy')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>
          )}
        </ModalDialog>
      </Modal>

      {/* Referral Analytics Modal */}
      <Modal open={analyticsModalOpen} onClose={() => setAnalyticsModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 560, width: '100%' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Referral Analytics
          </Typography>

          {analyticsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : analyticsError ? (
            <Alert color="danger">{analyticsError}</Alert>
          ) : !analyticsData ? (
            <Alert color="neutral">No analytics available.</Alert>
          ) : (
            <Grid container spacing={2}>
              {[
                { label: 'Total Referrals', value: analyticsData.total_referrals },
                { label: 'Successful Referrals', value: analyticsData.successful_referrals },
                {
                  label: 'Conversion Rate',
                  value: `${Number(analyticsData.conversion_rate).toFixed(1)}%`,
                },
                {
                  label: 'Total Commissions Earned',
                  value: `$${Number(analyticsData.total_commissions_earned).toLocaleString()}`,
                },
                {
                  label: 'Total Commissions Paid',
                  value: `$${Number(analyticsData.total_commissions_paid).toLocaleString()}`,
                },
                {
                  label: 'Pending Commissions',
                  value: `$${Number(analyticsData.pending_commissions).toLocaleString()}`,
                },
                {
                  label: 'Avg Commission / Referral',
                  value: `$${Number(analyticsData.average_commission_per_referral).toLocaleString()}`,
                },
              ].map((stat) => (
                <Grid xs={6} key={stat.label}>
                  <Card variant="soft" size="sm">
                    <CardContent>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {stat.label}
                      </Typography>
                      <Typography level="title-lg">{stat.value}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {analyticsData.period_start && analyticsData.period_end && (
                <Grid xs={12}>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                    Period: {format(new Date(analyticsData.period_start), 'MMM d, yyyy')} –{' '}
                    {format(new Date(analyticsData.period_end), 'MMM d, yyyy')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};